import { useState, useEffect, useRef, useCallback } from 'react';
import { Globe, Wand2, Save, CheckCircle, AlertTriangle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

// Auto-resizing textarea component
interface AutoResizeTextareaProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  minRows?: number;
}

function AutoResizeTextarea({ value, onChange, disabled, className, placeholder, minRows = 2 }: AutoResizeTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const minHeight = minRows * 24; // Approximate line height
      textarea.style.height = `${Math.max(textarea.scrollHeight, minHeight)}px`;
    }
  }, [minRows]);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={(e) => {
        onChange(e);
        adjustHeight();
      }}
      disabled={disabled}
      className={`${className} resize-none overflow-hidden`}
      placeholder={placeholder}
      rows={minRows}
    />
  );
}

interface LearningOutcomes {
  knowledge?: string[];
  skills?: string[];
  competence?: string[];
}

interface ContentModule {
  title: string;
  description?: string;
  learning_outcomes?: string[];
  hours?: number;
}

interface Translation {
  title_en: string;
  description_en: string;
  target_group_en: string;
  admission_requirements_en: string;
  teaching_learning_methods_en: string;
  assessment_criteria_en: string;
  learning_outcomes_en: LearningOutcomes;
  content_modules_en: ContentModule[];
  status: string;
  generated_at: string | null;
  approved: boolean;
  approved_at: string | null;
}

// content_modules can be either string[] (legacy) or ContentModule[] (modern)
type ContentModuleItem = string | ContentModule;

interface StudyPlan {
  id: string;
  title: string;
  description: string;
  target_group: string;
  admission_requirements: string;
  teaching_learning_methods: string;
  assessment_criteria: string;
  learning_outcomes: LearningOutcomes;
  content_modules: ContentModuleItem[];
  status: string;
}

// Helper to normalize content module for display
const normalizeModule = (module: ContentModuleItem): { title: string; description?: string } => {
  if (typeof module === 'string') {
    return { title: module };
  }
  return { title: module.title, description: module.description };
};

interface TranslationEditorProps {
  studyPlan: StudyPlan;
  onTranslationApproved?: () => void;
}

export default function TranslationEditor({ studyPlan, onTranslationApproved }: TranslationEditorProps) {
  const [translation, setTranslation] = useState<Translation | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic', 'outcomes']));

  // Fetch existing translation
  useEffect(() => {
    fetchTranslation();
  }, [studyPlan.id]);

  const fetchTranslation = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(`/api/study-plans/${studyPlan.id}/translation`);
      const data = await response.json();

      if (data.success) {
        setTranslation(data.translation);
      }
    } catch (err) {
      console.error('Error fetching translation:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateTranslation = async () => {
    try {
      setGenerating(true);
      setError(null);

      const response = await authenticatedFetch(`/api/study-plans/${studyPlan.id}/translate`, {
        method: 'POST'
      });

      const data = await response.json();

      if (data.success) {
        setTranslation({
          ...data.translation,
          status: 'draft',
          generated_at: new Date().toISOString(),
          approved: false,
          approved_at: null
        });
        setSuccess('Oversettelse generert! Gjennomgå og rediger før godkjenning.');
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(data.error || 'Kunne ikke generere oversettelse');
      }
    } catch (err) {
      setError('Feil ved generering av oversettelse');
      console.error('Error generating translation:', err);
    } finally {
      setGenerating(false);
    }
  };

  const saveTranslation = async () => {
    if (!translation) return;

    try {
      setSaving(true);
      setError(null);

      const response = await authenticatedFetch(`/api/study-plans/${studyPlan.id}/translation`, {
        method: 'PUT',
        body: JSON.stringify(translation)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Oversettelse lagret');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Kunne ikke lagre oversettelse');
      }
    } catch (err) {
      setError('Feil ved lagring av oversettelse');
      console.error('Error saving translation:', err);
    } finally {
      setSaving(false);
    }
  };

  const approveTranslation = async () => {
    try {
      setApproving(true);
      setError(null);

      const response = await authenticatedFetch(`/api/study-plans/${studyPlan.id}/translation/approve`, {
        method: 'POST'
      });

      const data = await response.json();

      if (data.success) {
        setTranslation(prev => prev ? {
          ...prev,
          status: 'approved',
          approved: true,
          approved_at: new Date().toISOString()
        } : null);
        setSuccess('Oversettelse godkjent! Den vil nå brukes i engelske attester.');
        onTranslationApproved?.();
      } else {
        setError(data.error || 'Kunne ikke godkjenne oversettelse');
      }
    } catch (err) {
      setError('Feil ved godkjenning av oversettelse');
      console.error('Error approving translation:', err);
    } finally {
      setApproving(false);
    }
  };

  const updateTranslationField = (field: keyof Translation, value: string) => {
    setTranslation(prev => prev ? { ...prev, [field]: value } : null);
  };

  const updateLearningOutcome = (category: keyof LearningOutcomes, index: number, value: string) => {
    setTranslation(prev => {
      if (!prev || !prev.learning_outcomes_en) return prev;
      const outcomes = { ...prev.learning_outcomes_en };
      if (outcomes[category]) {
        outcomes[category] = [...outcomes[category]!];
        outcomes[category]![index] = value;
      }
      return { ...prev, learning_outcomes_en: outcomes };
    });
  };

  const updateModule = (index: number, field: keyof ContentModule, value: string) => {
    setTranslation(prev => {
      if (!prev || !prev.content_modules_en) return prev;
      const modules = [...prev.content_modules_en];
      modules[index] = { ...modules[index], [field]: value };
      return { ...prev, content_modules_en: modules };
    });
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  if (studyPlan.status !== 'approved') {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 text-yellow-800">
          <AlertTriangle size={20} />
          <span>Oversettelse er kun tilgjengelig for godkjente studieplaner.</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  const hasTranslation = translation?.title_en;
  const isApproved = translation?.approved;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Globe className="text-kf-primary" size={24} />
          <div>
            <h3 className="text-lg font-semibold">Engelsk oversettelse</h3>
            <p className="text-sm text-gray-600">
              {!hasTranslation && 'Ingen oversettelse ennå'}
              {hasTranslation && !isApproved && 'Utkast - ikke godkjent'}
              {isApproved && 'Godkjent oversettelse'}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {!hasTranslation && (
            <button
              onClick={generateTranslation}
              disabled={generating}
              className="flex items-center space-x-2 px-4 py-2 bg-kf-primary text-white rounded-lg hover:bg-kf-secondary disabled:opacity-50"
            >
              {generating ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <Wand2 size={18} />
              )}
              <span>{generating ? 'Genererer...' : 'Generer med KI'}</span>
            </button>
          )}

          {hasTranslation && !isApproved && (
            <>
              <button
                onClick={generateTranslation}
                disabled={generating}
                className="flex items-center space-x-2 px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {generating ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <Wand2 size={16} />
                )}
                <span>Regenerer</span>
              </button>

              <button
                onClick={saveTranslation}
                disabled={saving}
                className="flex items-center space-x-2 px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <Save size={16} />
                )}
                <span>Lagre utkast</span>
              </button>

              <button
                onClick={approveTranslation}
                disabled={approving}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {approving ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <CheckCircle size={18} />
                )}
                <span>Godkjenn</span>
              </button>
            </>
          )}

          {isApproved && (
            <div className="flex items-center space-x-2 text-green-600">
              <CheckCircle size={20} />
              <span className="font-medium">Godkjent {translation?.approved_at && new Date(translation.approved_at).toLocaleDateString('nb-NO')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Translation Content */}
      {hasTranslation && (
        <div className="space-y-4">
          {/* Basic Info Section */}
          <div className="border rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('basic')}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100"
            >
              <span className="font-medium">Grunnleggende informasjon</span>
              {expandedSections.has('basic') ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>

            {expandedSections.has('basic') && (
              <div className="p-4 space-y-4">
                {/* Title */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Tittel (Norsk)</label>
                    <div className="p-3 bg-gray-50 rounded border text-gray-700">{studyPlan.title}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Title (English)</label>
                    <input
                      type="text"
                      value={translation.title_en || ''}
                      onChange={(e) => updateTranslationField('title_en', e.target.value)}
                      disabled={isApproved}
                      className="w-full p-3 border rounded focus:ring-2 focus:ring-kf-primary disabled:bg-gray-50"
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="grid grid-cols-2 gap-4 items-start">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Beskrivelse (Norsk)</label>
                    <div className="p-3 bg-gray-50 rounded border text-gray-700 whitespace-pre-wrap">{studyPlan.description}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Description (English)</label>
                    <AutoResizeTextarea
                      value={translation.description_en || ''}
                      onChange={(e) => updateTranslationField('description_en', e.target.value)}
                      disabled={isApproved}
                      minRows={3}
                      className="w-full p-3 border rounded focus:ring-2 focus:ring-kf-primary disabled:bg-gray-50"
                    />
                  </div>
                </div>

                {/* Target Group */}
                {studyPlan.target_group && (
                  <div className="grid grid-cols-2 gap-4 items-start">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Målgruppe (Norsk)</label>
                      <div className="p-3 bg-gray-50 rounded border text-gray-700 whitespace-pre-wrap">{studyPlan.target_group}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Target Group (English)</label>
                      <AutoResizeTextarea
                        value={translation.target_group_en || ''}
                        onChange={(e) => updateTranslationField('target_group_en', e.target.value)}
                        disabled={isApproved}
                        minRows={2}
                        className="w-full p-3 border rounded focus:ring-2 focus:ring-kf-primary disabled:bg-gray-50"
                      />
                    </div>
                  </div>
                )}

                {/* Admission Requirements */}
                {studyPlan.admission_requirements && (
                  <div className="grid grid-cols-2 gap-4 items-start">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Opptakskrav (Norsk)</label>
                      <div className="p-3 bg-gray-50 rounded border text-gray-700 whitespace-pre-wrap">{studyPlan.admission_requirements}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Admission Requirements (English)</label>
                      <AutoResizeTextarea
                        value={translation.admission_requirements_en || ''}
                        onChange={(e) => updateTranslationField('admission_requirements_en', e.target.value)}
                        disabled={isApproved}
                        minRows={2}
                        className="w-full p-3 border rounded focus:ring-2 focus:ring-kf-primary disabled:bg-gray-50"
                      />
                    </div>
                  </div>
                )}

                {/* Teaching/Learning Methods */}
                {studyPlan.teaching_learning_methods && (
                  <div className="grid grid-cols-2 gap-4 items-start">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Undervisningsformer (Norsk)</label>
                      <div className="p-3 bg-gray-50 rounded border text-gray-700 whitespace-pre-wrap">{studyPlan.teaching_learning_methods}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Teaching/Learning Methods (English)</label>
                      <AutoResizeTextarea
                        value={translation.teaching_learning_methods_en || ''}
                        onChange={(e) => updateTranslationField('teaching_learning_methods_en', e.target.value)}
                        disabled={isApproved}
                        minRows={2}
                        className="w-full p-3 border rounded focus:ring-2 focus:ring-kf-primary disabled:bg-gray-50"
                      />
                    </div>
                  </div>
                )}

                {/* Assessment Criteria */}
                {studyPlan.assessment_criteria && (
                  <div className="grid grid-cols-2 gap-4 items-start">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Vurderingskriterier (Norsk)</label>
                      <div className="p-3 bg-gray-50 rounded border text-gray-700 whitespace-pre-wrap">{studyPlan.assessment_criteria}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Assessment Criteria (English)</label>
                      <AutoResizeTextarea
                        value={translation.assessment_criteria_en || ''}
                        onChange={(e) => updateTranslationField('assessment_criteria_en', e.target.value)}
                        disabled={isApproved}
                        minRows={2}
                        className="w-full p-3 border rounded focus:ring-2 focus:ring-kf-primary disabled:bg-gray-50"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Learning Outcomes Section */}
          <div className="border rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('outcomes')}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100"
            >
              <span className="font-medium">Læringsutbytter / Learning Outcomes</span>
              {expandedSections.has('outcomes') ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>

            {expandedSections.has('outcomes') && translation.learning_outcomes_en && (
              <div className="p-4 space-y-6">
                {/* Knowledge */}
                {studyPlan.learning_outcomes?.knowledge && studyPlan.learning_outcomes.knowledge.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3">Kunnskaper / Knowledge</h4>
                    <div className="space-y-2">
                      {studyPlan.learning_outcomes.knowledge.map((item, index) => (
                        <div key={index} className="grid grid-cols-2 gap-4 items-start">
                          <div className="p-2 bg-gray-50 rounded border text-sm whitespace-pre-wrap">{item}</div>
                          <AutoResizeTextarea
                            value={translation.learning_outcomes_en?.knowledge?.[index] || ''}
                            onChange={(e) => updateLearningOutcome('knowledge', index, e.target.value)}
                            disabled={isApproved}
                            minRows={1}
                            className="p-2 border rounded text-sm focus:ring-2 focus:ring-kf-primary disabled:bg-gray-50"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Skills */}
                {studyPlan.learning_outcomes?.skills && studyPlan.learning_outcomes.skills.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3">Ferdigheter / Skills</h4>
                    <div className="space-y-2">
                      {studyPlan.learning_outcomes.skills.map((item, index) => (
                        <div key={index} className="grid grid-cols-2 gap-4 items-start">
                          <div className="p-2 bg-gray-50 rounded border text-sm whitespace-pre-wrap">{item}</div>
                          <AutoResizeTextarea
                            value={translation.learning_outcomes_en?.skills?.[index] || ''}
                            onChange={(e) => updateLearningOutcome('skills', index, e.target.value)}
                            disabled={isApproved}
                            minRows={1}
                            className="p-2 border rounded text-sm focus:ring-2 focus:ring-kf-primary disabled:bg-gray-50"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Competence */}
                {studyPlan.learning_outcomes?.competence && studyPlan.learning_outcomes.competence.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3">Generell kompetanse / General Competence</h4>
                    <div className="space-y-2">
                      {studyPlan.learning_outcomes.competence.map((item, index) => (
                        <div key={index} className="grid grid-cols-2 gap-4 items-start">
                          <div className="p-2 bg-gray-50 rounded border text-sm whitespace-pre-wrap">{item}</div>
                          <AutoResizeTextarea
                            value={translation.learning_outcomes_en?.competence?.[index] || ''}
                            onChange={(e) => updateLearningOutcome('competence', index, e.target.value)}
                            disabled={isApproved}
                            minRows={1}
                            className="p-2 border rounded text-sm focus:ring-2 focus:ring-kf-primary disabled:bg-gray-50"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Modules Section */}
          {studyPlan.content_modules && studyPlan.content_modules.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('modules')}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100"
              >
                <span className="font-medium">Moduler / Modules ({studyPlan.content_modules.length})</span>
                {expandedSections.has('modules') ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>

              {expandedSections.has('modules') && translation.content_modules_en && (
                <div className="p-4 space-y-4">
                  {studyPlan.content_modules.map((moduleItem, index) => {
                    const module = normalizeModule(moduleItem);
                    return (
                      <div key={index} className="border rounded p-4 space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Modul {index + 1} (Norsk)</label>
                            <div className="p-2 bg-gray-50 rounded border text-sm font-medium">{module.title}</div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Module {index + 1} (English)</label>
                            <input
                              type="text"
                              value={translation.content_modules_en[index]?.title || ''}
                              onChange={(e) => updateModule(index, 'title', e.target.value)}
                              disabled={isApproved}
                              className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-kf-primary disabled:bg-gray-50"
                            />
                          </div>
                        </div>
                        {module.description && (
                          <div className="grid grid-cols-2 gap-4 items-start">
                            <div className="p-2 bg-gray-50 rounded border text-sm whitespace-pre-wrap">{module.description}</div>
                            <AutoResizeTextarea
                              value={translation.content_modules_en[index]?.description || ''}
                              onChange={(e) => updateModule(index, 'description', e.target.value)}
                              disabled={isApproved}
                              minRows={2}
                              className="p-2 border rounded text-sm focus:ring-2 focus:ring-kf-primary disabled:bg-gray-50"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!hasTranslation && !generating && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
          <Globe className="mx-auto text-gray-400 mb-4" size={48} />
          <h4 className="text-lg font-medium text-gray-700 mb-2">Ingen engelsk oversettelse</h4>
          <p className="text-gray-500 mb-4">
            Generer en KI-oversettelse og rediger den før godkjenning.
          </p>
          <button
            onClick={generateTranslation}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-kf-primary text-white rounded-lg hover:bg-kf-secondary"
          >
            <Wand2 size={18} />
            <span>Generer oversettelse</span>
          </button>
        </div>
      )}
    </div>
  );
}
